import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';


// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Report to Sentry with additional context
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', true);
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.constructor.name
      });
      scope.setLevel('error');
      Sentry.captureException(error);
    });
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <ErrorFallback 
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={() => this.setState({ hasError: false, error: null, errorInfo: null })}
          showDetails={this.props.showDetails}
        />
      );
    }

    return this.props.children;
  }
}

// Error Fallback Component
const ErrorFallback = ({ error, errorInfo, resetError, showDetails = false }) => {
  const { t } = useTranslation();

  return (
    <div className="page-wrapper page-center">
      <div className="container container-tight py-4">
        <div className="text-center">
          <div className="mb-3">
            <div className="empty-header" style={{ fontSize: '4rem', color: '#dc3545' }}>⚠️</div>
          </div>
          <h1 className="mb-3">{t ? t('somethingWentWrong') : 'Something went wrong'}</h1>
          <p className="text-secondary mb-3">
            This error has been logged and reported. Please try refreshing the page or contact support if the problem persists.
          </p>
          
          <div className="mb-3">
            <button 
              className="btn btn-primary me-2" 
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
            <button 
              className="btn btn-outline-primary" 
              onClick={() => window.history.back()}
            >
                Go Back
            </button>
          </div>

          {showDetails && error && (
            <details className="text-start">
              <summary className="btn btn-link p-0 mb-2 text-center">
                Technical Details
              </summary>
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title text-danger">Error:</h6>
                  <pre className="text-wrap small">{error.toString()}</pre>
                  
                  {errorInfo && (
                    <>
                      <h6 className="card-title text-danger mt-3">Stack Trace:</h6>
                      <pre className="text-wrap small">{errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  showDetails: PropTypes.bool
};

ErrorFallback.propTypes = {
  error: PropTypes.object,
  errorInfo: PropTypes.object,
  resetError: PropTypes.func.isRequired,
  showDetails: PropTypes.bool
};

export default ErrorBoundary;